/**
 * Consolidation governance endpoints (PRD-0246 D-7) — run/proposal reads,
 * apply/dismiss decisions, episode un-archival and the pause policy. All ten
 * routes keep their trailing slash (the claimReview.ts convention — the
 * backend registers slashed routes only).
 *
 * If-Match is REQUIRED on apply/dismiss (the claimReview.ts multi-reviewer
 * decision shape via `ifMatchHeaders`) and OPTIONAL on the policy PUT (the
 * updateCompositionPolicy shape via `optionalIfMatchHeaders` — the endpoint's
 * own `if_match: Optional[int]` header, unlike activation-policy's required
 * one). Unarchive carries no If-Match at all: the archived/not-archived
 * state check is its own conflict guard (R-7), so there is no version token
 * to send.
 */

import type {
  ConsolidationEpisodeUnarchiveRequest,
  ConsolidationMetrics,
  ConsolidationPolicy,
  ConsolidationPolicyUpdate,
  ConsolidationProposal,
  ConsolidationProposalApplyRequest,
  ConsolidationProposalApplyResponse,
  ConsolidationProposalDismissRequest,
  ConsolidationProposalList,
  ConsolidationProposalListFilters,
  ConsolidationRunDetail,
  ConsolidationRunList,
  EpisodeResponse,
} from '@/types/smem'
import { apiClient } from './client'
import { ifMatchHeaders, optionalIfMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const consolidation = {
  listRuns: async (
    params: { skip?: number; limit?: number } = {}
  ): Promise<ConsolidationRunList> => {
    const response = await apiClient.get<ConsolidationRunList>(
      '/api/v1/semantic-memory/consolidation/runs/',
      { params }
    )
    return response.data
  },

  getRun: async (runId: string): Promise<ConsolidationRunDetail> => {
    const response = await apiClient.get<ConsolidationRunDetail>(
      `/api/v1/semantic-memory/consolidation/runs/${runId}/`
    )
    return response.data
  },

  metrics: async (): Promise<ConsolidationMetrics> => {
    const response = await apiClient.get<ConsolidationMetrics>(
      '/api/v1/semantic-memory/consolidation/metrics/'
    )
    return response.data
  },

  listProposals: async (
    filters?: ConsolidationProposalListFilters
  ): Promise<ConsolidationProposalList> => {
    const response = await apiClient.get<ConsolidationProposalList>(
      '/api/v1/semantic-memory/consolidation/proposals/',
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  getProposal: async (id: string): Promise<ConsolidationProposal> => {
    const response = await apiClient.get<ConsolidationProposal>(
      `/api/v1/semantic-memory/consolidation/proposals/${id}/`
    )
    return response.data
  },

  applyProposal: async (
    id: string,
    ifMatch: number,
    body: ConsolidationProposalApplyRequest
  ): Promise<ConsolidationProposalApplyResponse> => {
    const response = await apiClient.post<ConsolidationProposalApplyResponse>(
      `/api/v1/semantic-memory/consolidation/proposals/${id}/apply/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  dismissProposal: async (
    id: string,
    ifMatch: number,
    body: ConsolidationProposalDismissRequest
  ): Promise<ConsolidationProposal> => {
    const response = await apiClient.post<ConsolidationProposal>(
      `/api/v1/semantic-memory/consolidation/proposals/${id}/dismiss/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  unarchiveEpisode: async (
    episodeId: string,
    body: ConsolidationEpisodeUnarchiveRequest
  ): Promise<EpisodeResponse> => {
    const response = await apiClient.post<EpisodeResponse>(
      `/api/v1/semantic-memory/consolidation/episodes/${episodeId}/unarchive/`,
      body
    )
    return response.data
  },

  getPolicy: async (): Promise<ConsolidationPolicy> => {
    const response = await apiClient.get<ConsolidationPolicy>(
      '/api/v1/semantic-memory/consolidation/policy/'
    )
    return response.data
  },

  updatePolicy: async (
    body: ConsolidationPolicyUpdate,
    ifMatch?: number
  ): Promise<ConsolidationPolicy> => {
    const response = await apiClient.put<ConsolidationPolicy>(
      '/api/v1/semantic-memory/consolidation/policy/',
      body,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },
}
